/**
 * Value Estimator runner — the `estimate_value` behavior of the agent template.
 *
 * One item, one attribute (PRD-value-suggestion-agent.md §4.1):
 *
 *   pre-check → gather evidence → estimate → write proposal → decision record
 *
 * and the batch over a scope (§4.3). The proposal goes through the SAME store
 * function a person's proposal uses, so acceptance, provenance and the review
 * screen need nothing new. Every outcome — proposed, abstained, skipped,
 * error — is written to agent_decision_records so it can be explained later.
 *
 * Voice, never authority: this file writes value_proposals and
 * agent_decision_records only. It never touches accepted_values.
 */

import { getSupabaseClient } from './supabaseClient';
import { getAgentSettings, type AgentSettings } from './agentSettingsService';
import { fetchValuesForAgent } from './valueAiService';
import type { BatchStats, RunScope } from './agentRunService';
import { currentAgentRunId } from './agentRunService';
import { fetchItemsForScope } from './agentScopeItems';
import { AgentLlmError } from './llmClient';
import { scoreItemClarity } from './goodItemTest';
import { loadPrecedentCandidates, rankPrecedents } from './valueEstimatorPrecedent';
import { calibrateConfidence, loadAgentCalibration, type CalibrationRow } from './agentCalibrationService';
import { approvedRuleTexts, loadLearnedRules, type LearnedRule } from './agentLearnedRulesService';
import { loadConnectedHistory, type ConnectedHistoryLine } from './valueEstimatorConnected';
import {
  runValueEstimator,
  type EstimateInput,
  type EstimatePrecedent,
  type EstimateReferenceDoc,
  type EstimateResult,
} from './llmValueEstimatorService';
import {
  VALUE_ESTIMATOR,
  VALUE_ESTIMATOR_AGENT_TYPE,
  agentReportHref,
  assertAgentMayProduce,
  type ValueEstimatorBehaviorConfig,
} from './ai/agentDefinitions';
import { resolveEstimatorConfigFrom, resolveModelParams, resolveScaleFrom, type ResolvedScale, type ScaledAttribute } from '../../supabase/functions/_shared/estimateValueCore.ts';
import { createAgentTask, enqueueAgentDelivery, countAgentModelCall } from './agentRuntimeService';
import { isWorkspaceEntitledToAi } from './ai/aiAssistSettings';
import { useAttributesStore } from '../stores/prioritization/attributesStore';
import { useValueProposalStore } from '../stores/prioritization/valueProposalStore';
import type { Attribute } from '../types/prioritization/lifecycle';

// ── Identity ──────────────────────────────────────────────────────────────────

/**
 * Which agent is running this behavior: the built-in Value Estimator, or a
 * custom agent an administrator made on it (agent type `custom_<id>`, spec
 * §2 J). Settings, runs, decision records, calibration, learned rules and the
 * proposer id are all keyed by the agent type, so each agent keeps its own.
 */
export interface EstimateAgent {
  agentType: string;
  name: string;
}

export const VALUE_ESTIMATOR_PROPOSER_NAME = 'Value Estimator';
export const VALUE_ESTIMATOR_IDENTITY: EstimateAgent = { agentType: VALUE_ESTIMATOR_AGENT_TYPE, name: VALUE_ESTIMATOR_PROPOSER_NAME };
/** Bump when the prompt or the evidence assembly changes; stored on every record. */
export const VALUE_ESTIMATOR_STACK_VERSION = 'value_estimator/2';

/** value_proposals.proposed_by for an agent. The triggers key on the `agent:` prefix. */
export function agentProposerId(agentType: string): string {
  return `agent:${agentType}`;
}

/** value_proposals.proposed_by for the built-in Value Estimator. */
export const VALUE_ESTIMATOR_PROPOSER_ID = agentProposerId(VALUE_ESTIMATOR_AGENT_TYPE);

/** True for a value proposal written by any agent — one definition, in lib/valueStatus. */
export { isAgentProposer as isAgentValueProposer } from './valueStatus';

// ── Config ────────────────────────────────────────────────────────────────────

export function resolveEstimatorConfig(settings: AgentSettings | null): ValueEstimatorBehaviorConfig {
  return resolveEstimatorConfigFrom((settings?.behaviorConfig ?? {}) as Partial<ValueEstimatorBehaviorConfig>);
}

/** What the delivery switches say, with the definition's defaults where nothing is stored. */
export function resolveDelivery(settings: AgentSettings | null): typeof VALUE_ESTIMATOR.delivery {
  const raw = (settings?.delivery ?? {}) as Partial<typeof VALUE_ESTIMATOR.delivery>;
  return { ...VALUE_ESTIMATOR.delivery, ...raw };
}

/** Which evidence sources are on: the settings row wins, else the definition's default. */
export function resolveEvidenceSources(settings: AgentSettings | null): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const source of VALUE_ESTIMATOR.evidenceSources) {
    const stored = settings?.evidenceSources?.[source.key];
    out[source.key] = source.available && (typeof stored === 'boolean' ? stored : source.defaultEnabled);
  }
  return out;
}

// ── Scale ─────────────────────────────────────────────────────────────────────

export type { ResolvedScale } from '../../supabase/functions/_shared/estimateValueCore.ts';

/**
 * The scale the estimate is made on (the behavior's shared core, one copy with
 * the server runtime).
 */
export function resolveScale(attr: Attribute): ResolvedScale | null {
  return resolveScaleFrom(attr as unknown as ScaledAttribute);
}

// ── Reads ─────────────────────────────────────────────────────────────────────

interface ItemRow {
  id: string;
  workspace_id: string;
  owner_id: string | null;
  title: string;
  description: string | null;
  item_category: string | null;
  altitude_level: number | null;
  exit_status: string | null;
}

async function loadItem(itemId: string): Promise<ItemRow | null> {
  const { data } = await getSupabaseClient()
    .from('items')
    .select('id, workspace_id, owner_id, title, description, item_category, altitude_level, exit_status')
    .eq('id', itemId)
    .maybeSingle();
  return (data as ItemRow | null) ?? null;
}

async function hasAcceptedValue(itemId: string, attributeKey: string, scaleId: string | null): Promise<boolean> {
  let q = getSupabaseClient()
    .from('accepted_values')
    .select('id')
    .eq('item_id', itemId)
    .eq('attribute_key', attributeKey)
    .is('session_scope', null)
    .limit(1);
  q = scaleId ? q.eq('scale_id', scaleId) : q.is('scale_id', null);
  const { data } = await q;
  return (data?.length ?? 0) > 0;
}

async function hasOpenAgentProposal(proposerId: string, itemId: string, attributeKey: string, scaleId: string | null): Promise<boolean> {
  let q = getSupabaseClient()
    .from('value_proposals')
    .select('id')
    .eq('item_id', itemId)
    .eq('attribute_key', attributeKey)
    .eq('proposed_by', proposerId)
    .eq('status', 'proposed')
    .limit(1);
  q = scaleId ? q.eq('scale_id', scaleId) : q.is('scale_id', null);
  const { data } = await q;
  return (data?.length ?? 0) > 0;
}

function referenceDocsFrom(settings: AgentSettings | null): EstimateReferenceDoc[] {
  return (settings?.referenceDocs ?? [])
    .filter((d) => d && typeof d.content === 'string' && d.content.trim())
    .map((d) => ({ id: d.id, title: d.title, content: d.content }));
}

// ── Decision record ───────────────────────────────────────────────────────────

interface DecisionRecordInput {
  agentType: string;
  taskId?: string | null;
  workspaceId: string;
  runId?: string | null;
  itemId: string;
  attributeKey: string;
  outputKind: 'proposal:value' | 'abstain' | 'skipped' | 'error';
  outputId?: string | null;
  proposedValue?: unknown;
  confidence?: number | null;
  rationale: string;
  evidence: unknown[];
  model?: string | null;
  sponsorUserId?: string | null;
  runByUserId?: string | null;
}

async function writeDecisionRecord(input: DecisionRecordInput): Promise<void> {
  // The one write path (migration 824): the function checks membership and fixes the identity.
  const { error } = await getSupabaseClient().rpc('record_agent_decision', {
    p_workspace_id: input.workspaceId,
    p_agent_type: input.agentType,
    p_object_id: input.itemId,
    p_output_kind: input.outputKind,
    p_rationale: input.rationale,
    p_object_type: 'item',
    p_field: input.attributeKey,
    p_output_id: input.outputId ?? null,
    p_proposed_value: input.proposedValue ?? null,
    p_confidence: input.confidence ?? null,
    p_evidence: input.evidence,
    p_stack_version: VALUE_ESTIMATOR_STACK_VERSION,
    p_model: input.model ?? null,
    p_run_id: input.runId ?? null,
    p_sponsor_user_id: input.sponsorUserId ?? null,
    p_run_by_user_id: input.runByUserId ?? null,
    p_task_id: input.taskId ?? null,
  });
  if (error) console.warn('[ValueEstimator] decision record not written:', error.message);
}

// ── One item, one attribute ───────────────────────────────────────────────────

export type EstimateOutcome =
  | { outcome: 'preview'; value: string | null; reason?: string }
  | { outcome: 'proposed'; proposalId: string; value: string; confidence: number; rationale: string; modelOnly: boolean }
  | { outcome: 'abstained'; reason: string }
  | { outcome: 'skipped'; reason: string }
  | { outcome: 'error'; reason: string };

export interface EstimateParams {
  /** The agent running the behavior; the built-in Value Estimator when absent. */
  agent?: EstimateAgent;
  workspaceId: string;
  itemId: string;
  attributeKey: string;
  /** The agent_run_logs row for a batch; null for a single manual run. */
  runId?: string | null;
  /** The person who asked (manual) — attribution on the record. */
  runByUserId?: string | null;
  /** Pass settings when the caller already has them (batch); else they are read. */
  settings?: AgentSettings | null;
  /** Pass the reliability table when the caller already has it (batch); else it is read. */
  calibration?: CalibrationRow[];
  /** Pass the learned rules when the caller already has them (batch); else they are read. */
  rules?: LearnedRule[];
  /** Announce a proposal to the agent's audience. A batch announces once, at the end. */
  deliver?: boolean;
  /**
   * The learned-rule check (spec §2 I): answer as if these were the approved
   * rules, on an item that already has an accepted value, and write nothing.
   */
  preview?: { rules: string[] };
  /** Values this agent works on (migration 920), when the caller already has them (batch); else they are read. */
  allowedAttributes?: string[];
  /**
   * A person asked for a recommendation on purpose ("Ask <agent>" on the item, owner
   * 2026-10-21): answer even when the value already has an official value. The answer
   * waits beside it as a recommendation; nothing changes until a person accepts it.
   * Batch and scheduled runs leave this off and skip settled values.
   */
  askAgain?: boolean;
}

export async function estimateValueForItem(params: EstimateParams): Promise<EstimateOutcome> {
  const agent = params.agent ?? VALUE_ESTIMATOR_IDENTITY;
  const proposerId = agentProposerId(agent.agentType);
  const settings =
    params.settings !== undefined
      ? params.settings
      : (await getAgentSettings(params.workspaceId, agent.agentType)).data;
  if (!settings?.enabled) return { outcome: 'skipped', reason: `${agent.name} is off for this workspace.` };
  if (!isWorkspaceEntitledToAi(params.workspaceId)) return { outcome: 'skipped', reason: 'This workspace\'s plan does not include AI features.' };

  const config = resolveEstimatorConfig(settings);
  // Which values this agent works on is the value's own AI setting (migration 920).
  const allowed = params.allowedAttributes ?? (await fetchValuesForAgent(params.workspaceId, agent.agentType).catch(() => []));
  if (!allowed.includes(params.attributeKey)) {
    return { outcome: 'skipped', reason: `${params.attributeKey} is not set to use ${agent.name} (Settings → Attributes → AI).` };
  }

  const attr = useAttributesStore.getState().attributes.find((a) => a.key === params.attributeKey);
  if (!attr) return { outcome: 'error', reason: `Attribute ${params.attributeKey} is not defined in this workspace.` };
  const scale = resolveScale(attr);

  const delivery = resolveDelivery(settings);
  const preview = params.preview ?? null;
  const record = (input: DecisionRecordInput) => (preview ? Promise.resolve() : writeDecisionRecord(input));
  const base = {
    agentType: agent.agentType,
    workspaceId: params.workspaceId,
    runId: params.runId ?? null,
    itemId: params.itemId,
    attributeKey: params.attributeKey,
    model: settings.model,
    sponsorUserId: settings.sponsorUserId ?? null,
    runByUserId: params.runByUserId ?? null,
  };

  if (!scale) {
    const reason = `${attr.label} has no scale or options to choose from.`;
    await record({ ...base, outputKind: 'abstain', rationale: reason, evidence: [] });
    return { outcome: 'abstained', reason };
  }

  const item = await loadItem(params.itemId);
  if (!item) return { outcome: 'error', reason: 'Item not found.' };
  if (item.workspace_id !== params.workspaceId) return { outcome: 'error', reason: 'Item is outside this workspace.' };

  // Idempotency: an accepted value, or an open agent proposal, means the fact is settled or pending
  // (a person asking again on purpose passes the first; nothing passes the second).
  if (!preview && !params.askAgain && await hasAcceptedValue(item.id, attr.key, scale.scaleId)) {
    return { outcome: 'skipped', reason: `${attr.label} already has an accepted value.` };
  }
  if (!preview && await hasOpenAgentProposal(proposerId, item.id, attr.key, scale.scaleId)) {
    return { outcome: 'skipped', reason: `An estimate from ${agent.name} is already waiting for review.` };
  }

  // Pre-check: a thin item abstains before any model call.
  const clarity = scoreItemClarity(item.title ?? '', item.description ?? '');
  if (clarity.overall < config.clarityFloor) {
    const missing = clarity.suggestions.length ? clarity.suggestions.join('; ').toLowerCase() : 'a clearer title and description';
    const reason = `Cannot estimate ${attr.label.toLowerCase()} yet: ${missing}.`;
    await record({ ...base, outputKind: 'abstain', rationale: reason, evidence: [{ kind: 'clarity', score: clarity.overall }] });
    // When the administrator turned it on, ask the item's owner to say more (spec §2 D: task output).
    if (preview) return { outcome: 'preview', value: null, reason };
    if (delivery.createTasks && item.owner_id) {
      assertAgentMayProduce(VALUE_ESTIMATOR, 'task');
      await createAgentTask({
        workspaceId: params.workspaceId,
        agentType: agent.agentType,
        kind: 'clarify',
        itemIds: [item.id],
        assigneeId: item.owner_id,
        name: `Say more about "${item.title}" so ${agent.name} can estimate it`,
        description: reason,
        attributeKey: attr.key,
      });
    }
    return { outcome: 'abstained', reason };
  }

  // Evidence, in trust order.
  const sources = resolveEvidenceSources(settings);
  let precedents: EstimatePrecedent[] = [];
  if (sources.workspace_record) {
    const { candidates } = await loadPrecedentCandidates({
      workspaceId: params.workspaceId,
      attributeKey: attr.key,
      scaleId: scale.scaleId,
      excludeItemId: item.id,
    });
    precedents = rankPrecedents(
      { itemId: item.id, title: item.title, description: item.description, category: item.item_category, altitudeLevel: item.altitude_level },
      candidates,
    );
  }
  const referenceDocs = sources.reference_docs ? referenceDocsFrom(settings) : [];
  const connected: ConnectedHistoryLine[] = sources.connected_systems
    ? await loadConnectedHistory({ workspaceId: params.workspaceId, itemId: item.id, itemTitle: item.title })
    : [];
  const allRules = params.rules ?? (await loadLearnedRules(params.workspaceId, agent.agentType));
  const rules = preview ? preview.rules : approvedRuleTexts(allRules, attr.key);

  // Every model call counts against the agent's monthly cap (spec §2 K).
  const meter = await countAgentModelCall(params.workspaceId, agent.agentType);
  if (!meter.allowed) {
    const reason = meter.error ?? `${agent.name} has used its ${meter.limit} model calls for this month. An administrator can raise the cap on its settings page.`;
    await record({ ...base, outputKind: 'error', rationale: reason, evidence: [] });
    return { outcome: 'error', reason };
  }

  const input: EstimateInput = {
    item: { id: item.id, title: item.title, description: item.description, category: item.item_category, altitudeLevel: item.altitude_level },
    attribute: { key: attr.key, label: attr.label, definition: attr.definition ?? null },
    scale: { id: scale.scaleId, label: scale.label, type: scale.type, points: scale.points },
    precedents,
    connected,
    referenceDocs,
    rules,
    allowModelOnly: config.allowModelOnly,
    model: settings.model,
    systemPrompt: settings.systemPrompt ?? null,
    modelParams: resolveModelParams(VALUE_ESTIMATOR.modelDefaults, settings.modelParams),
    timeoutMs: Number((settings.limits as { timeoutMs?: number } | undefined)?.timeoutMs ?? VALUE_ESTIMATOR.limits.timeoutMs),
  };

  let result: EstimateResult;
  try {
    result = await runValueEstimator(input);
  } catch (err) {
    const reason = err instanceof AgentLlmError ? err.message : err instanceof Error ? err.message : 'The model call failed.';
    await record({ ...base, outputKind: 'error', rationale: reason, evidence: [] });
    return { outcome: 'error', reason };
  }

  const evidence = [
    ...precedents.map((p) => ({ kind: 'precedent', id: p.itemId, label: p.title, value: p.value, similarity: p.similarity })),
    ...connected.map((c) => ({ kind: 'connected', id: c.id, label: c.summary, linked: c.linked, points: c.storyPoints, spentHours: c.spentHours, cycleDays: c.cycleDays })),
    ...referenceDocs.map((d) => ({ kind: 'reference_doc', id: d.id, label: d.title })),
    ...allRules.filter((x) => x.status === 'approved' && (x.field == null || x.field === attr.key)).map((x) => ({ kind: 'rule', id: x.id, label: x.ruleText, version: x.version })),
  ];

  if (preview) return { outcome: 'preview', value: result.kind === 'estimate' ? result.value : null, reason: result.kind === 'abstain' ? result.reason : undefined };

  if (result.kind === 'abstain') {
    await record({ ...base, outputKind: 'abstain', rationale: result.reason, evidence });
    return { outcome: 'abstained', reason: result.reason };
  }

  // Calibration (PRD §6.1): once this bucket has enough reviews, the confidence shown is
  // what this confidence has actually meant here, not what the model claimed.
  const calibrationRows = params.calibration ?? (await loadAgentCalibration(params.workspaceId, agent.agentType));
  const calibrated = calibrateConfidence(result.confidenceScore, calibrationRows, attr.key);
  const confidence = calibrated.confidence;
  const rationale = calibrated.calibrated
    ? `${result.rationale} (Confidence calibrated: proposals this sure were accepted unchanged ${Math.round(confidence * 100)}% of the time here, over ${calibrated.reviewed} reviews.)`
    : result.rationale;

  if (confidence < config.confidenceFloor) {
    const reason = `Confidence ${Math.round(confidence * 100)}% is below this workspace's floor of ${Math.round(config.confidenceFloor * 100)}%. ${rationale}`;
    await record({ ...base, outputKind: 'abstain', proposedValue: result.value, confidence, rationale: reason, evidence });
    return { outcome: 'abstained', reason };
  }

  // The same write path a person's proposal takes.
  let proposalId: string;
  try {
    assertAgentMayProduce(VALUE_ESTIMATOR, 'proposal:value');
    const proposal = await useValueProposalStore.getState().proposeValue({
      itemId: item.id,
      attributeKey: attr.key,
      scaleId: scale.scaleId,
      proposedValue: result.value,
      proposedBy: proposerId,
      proposedByName: agent.name,
      proposerRole: 'agent',
      confidence,
      rationale,
      valueKind: config.valueKind,
    });
    proposalId = proposal.id;
  } catch (err) {
    const reason = err instanceof Error ? err.message : 'The proposal could not be saved.';
    await record({ ...base, outputKind: 'error', proposedValue: result.value, confidence, rationale: reason, evidence });
    return { outcome: 'error', reason };
  }

  await record({
    ...base,
    outputKind: 'proposal:value',
    outputId: proposalId,
    proposedValue: result.value,
    // The model's own claim is what calibration is measured against; store it, not the calibrated figure.
    confidence: result.confidenceScore,
    rationale,
    evidence: evidence.map((e) => ({ ...e, used: result.evidenceUsed.includes(e.id) })),
  });

  if (params.deliver !== false && confidence >= (delivery.confidenceFloor ?? 0)) {
    await enqueueAgentDelivery({
      workspaceId: params.workspaceId,
      agentType: agent.agentType,
      title: `${agent.name} proposed ${attr.label.toLowerCase()} ${result.value} for "${item.title}"`,
      body: rationale,
      link: `/prioritization/items/${item.id}?tab=values`,
      itemIds: [item.id],
      runId: params.runId ?? null,
      confidence,
    });
  }

  return {
    outcome: 'proposed',
    proposalId,
    value: result.value,
    confidence,
    rationale,
    modelOnly: result.modelOnly,
  };
}

// ── Batch over a scope ────────────────────────────────────────────────────────



/**
 * Run over a scope. Matches AgentBatchRunner so the Agents hub and the run
 * panel can drive it. Stats: processed = item×attribute pairs the agent looked
 * at (proposed, abstained or skipped); proposed = proposals written;
 * skipped = already settled or pending; errors = model or save failures.
 */
export async function runValueEstimatorBatch(
  workspaceId: string,
  scope: RunScope,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
  runByUserId?: string | null,
  agent: EstimateAgent = VALUE_ESTIMATOR_IDENTITY,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };
  const { data: settings } = await getAgentSettings(workspaceId, agent.agentType);
  if (!settings?.enabled) return stats;
  const cap = Number((settings.limits as { perRunObjectCap?: number } | undefined)?.perRunObjectCap ?? VALUE_ESTIMATOR.limits.perRunObjectCap);

  const { items, error } = await fetchItemsForScope(workspaceId, scope, {
    agentType: agent.agentType,
    exitStatuses: ['active'],
    limit: cap,
  });
  if (error) {
    console.warn('[ValueEstimator] scope error:', error);
    stats.errors += 1;
    return stats;
  }
  stats.itemsRead = items.length;
  const ids = items.map((i) => i.id);
  const runId = await currentAgentRunId(workspaceId, agent.agentType);
  const calibration = await loadAgentCalibration(workspaceId, agent.agentType);
  const rules = await loadLearnedRules(workspaceId, agent.agentType);
  // The values whose AI setting names this agent (migration 920).
  const allowedAttributes = await fetchValuesForAgent(workspaceId, agent.agentType).catch(() => [] as string[]);
  const total = ids.length * allowedAttributes.length;
  let done = 0;
  const proposedItems = new Set<string>();
  onProgress(0, total);

  // Items per batch (spec §2 K): that many item×attribute pairs run at once.
  const batchSize = Math.max(1, Math.min(25, Number((settings.limits as { batchSize?: number } | undefined)?.batchSize ?? VALUE_ESTIMATOR.limits.batchSize)));
  const failStop = (settings.limits as { failurePosture?: string } | undefined)?.failurePosture === 'fail_run';
  const pairs = ids.flatMap((itemId) => allowedAttributes.map((attributeKey) => ({ itemId, attributeKey })));
  for (let i = 0; i < pairs.length; i += batchSize) {
    if (signal.aborted) return stats;
    const chunk = pairs.slice(i, i + batchSize);
    const outcomes = await Promise.all(chunk.map(({ itemId, attributeKey }) =>
      estimateValueForItem({ agent, workspaceId, itemId, attributeKey, runId, runByUserId, settings, calibration, rules, allowedAttributes, deliver: false })));
    let failed = false;
    outcomes.forEach((outcome, j) => {
      if (outcome.outcome === 'proposed') { stats.processed += 1; stats.proposed += 1; proposedItems.add(chunk[j].itemId); }
      else if (outcome.outcome === 'abstained') { stats.processed += 1; }
      else if (outcome.outcome === 'skipped') { stats.skipped += 1; }
      else { stats.errors += 1; failed = true; }
    });
    done += chunk.length;
    onProgress(done, total);
    // Failure posture (spec §2 K): count and move on, or stop the run at the first failure.
    if (failed && failStop) return stats;
  }
  // One announcement per run, to the agent's audience, by its digest setting (spec §2 F).
  if (proposedItems.size > 0) {
    const reportHref = agentReportHref(agent.agentType);
    await enqueueAgentDelivery({
      workspaceId,
      agentType: agent.agentType,
      title: `${agent.name} proposed values for ${proposedItems.size} item${proposedItems.size === 1 ? '' : 's'}`,
      body: `${stats.proposed} proposal${stats.proposed === 1 ? '' : 's'} are waiting for review.`,
      link: reportHref,
      itemIds: [...proposedItems],
      runId,
    });
  }
  return stats;
}
